<template>
  <div class="layout-container">
    <SideMenu :isMobileMenuOpen="isMobileMenuOpen" @closeMobileMenu="closeMobileMenu" />

    <div class="menu-indicator" @click="toggleMobileMenu" v-if="!isMobileMenuOpen && isMobileView">
      <div class="indicator-arrow">›</div>
      <div class="touch-area"></div>
    </div>

    <main class="main-content" :class="{ 'main-content--orphan-admin': orphanAdminBlocked || licenseBlocked }">
      <div
        v-if="viewerReadOnlyBanner"
        class="viewer-readonly-banner"
        role="status"
      >
        <font-awesome-icon icon="eye" class="viewer-readonly-banner__icon" />
        <span>{{ $t('navigation.viewerModeBanner') }}</span>
      </div>
      <div
        v-if="licenseWarningBanner"
        class="license-invalid-banner"
        role="status"
      >
        <font-awesome-icon icon="id-badge" class="license-invalid-banner__icon" />
        <span>{{ licenseBannerText }}</span>
      </div>
      <div class="main-content-inner" :class="{ 'main-content-inner--blurred': orphanAdminBlocked || licenseBlocked }">
        <router-view :key="projectScopedViewKey"></router-view>
      </div>
      <div v-if="orphanAdminBlocked" class="main-content-block-overlay" role="alert">
        <div class="main-content-block-card">
          <p class="main-content-block-title">{{ $t('navigation.noProjectMembershipTitle') }}</p>
          <p class="main-content-block-text">{{ $t('navigation.noProjectMembershipMessage') }}</p>
        </div>
      </div>
      <div v-else-if="licenseBlocked" class="main-content-block-overlay" role="alert">
        <div class="main-content-block-card">
          <p class="main-content-block-title">{{ $t('navigation.licenseInvalidTitle') }}</p>
          <p class="main-content-block-text">{{ $t('navigation.licenseInvalidMessage') }}</p>
          <router-link to="/profile" class="license-block-link">{{ $t('navigation.licenseInvalidGoProfile') }}</router-link>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useProjectContextStore } from '@/stores/projectContext'
import { useProjectPermissions } from '@/composables/useProjectPermissions'
import { isLicenseExemptPath, useLicenseStatus } from '@/composables/useLicenseStatus'
import SideMenu from './SideMenu.vue'

const { t } = useI18n()

const isMobileMenuOpen = ref(false)
const isMobileView = ref(false)
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const projectContext = useProjectContextStore()
const { isProjectViewer } = useProjectPermissions()
const { isLicenseValid, licenseBlockReason } = useLicenseStatus()

const viewerReadOnlyBanner = computed(
  () =>
    isProjectViewer.value &&
    projectContext.projectsLoaded &&
    projectContext.projects.length > 0
)

const projectScopedViewKey = computed(() => {
  if (auth.user?.role === 'superadmin') {
    return `${route.path}::${projectContext.activeProjectId ?? 'no-project'}`
  }
  return route.path
})

const orphanAdminBlocked = computed(
  () =>
    auth.user?.role === 'admin' &&
    projectContext.projectsLoaded &&
    projectContext.projects.length === 0
)

const licenseBlocked = computed(
  () =>
    auth.user?.role === 'admin' &&
    !isLicenseValid.value &&
    !isLicenseExemptPath(route.path)
)

const licenseWarningBanner = computed(
  () => auth.user?.role === 'admin' && !isLicenseValid.value
)

const licenseBannerText = computed(() => {
  const reason = licenseBlockReason.value ?? 'inactive'
  const key = `license.banner.${reason}`
  const translated = t(key)
  return translated !== key ? translated : t('license.banner.inactive')
})

watch(
  () => [projectContext.projectsLoaded, projectContext.projects.length, route.path] as const,
  () => {
    if (auth.user?.role !== 'superadmin') return
    if (!projectContext.projectsLoaded || projectContext.projects.length > 0) return
    const p = route.path
    if (p === '/profile' || p === '/admins' || p.startsWith('/admins/')) return
    void router.replace({ path: '/admins', query: { tab: 'projects' } })
  }
)

watch(
  () => [orphanAdminBlocked.value, licenseBlocked.value, isMobileMenuOpen.value] as const,
  ([blocked, licenseBlock, menuOpen]) => {
    if ((blocked || licenseBlock) && !menuOpen) {
      document.body.style.overflow = 'hidden'
    } else if (!menuOpen) {
      document.body.style.overflow = ''
    }
  },
  { immediate: true }
)

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  if (isMobileMenuOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  document.body.style.overflow = ''
}

const checkIfMobile = () => {
  isMobileView.value = window.innerWidth <= 768
}

const handleResize = () => {
  checkIfMobile()
  if (window.innerWidth > 768 && isMobileMenuOpen.value) {
    closeMobileMenu()
  }
}

const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isMobileMenuOpen.value) {
    closeMobileMenu()
  }
}

onMounted(() => {
  checkIfMobile()
  window.addEventListener('resize', handleResize)
  document.addEventListener('keydown', handleEscKey)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('keydown', handleEscKey)
  document.body.style.overflow = ''
})
</script>

<style scoped>
.layout-container {
  display: flex;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  position: relative;
}

.menu-indicator {
  display: none;
  position: fixed;
  top: 10px;
  right: 0;
  width: 18px;
  height: 40px;
  background-color: #D8670C;
  border-radius: 5px 0 0 5px;
  z-index: 35;
  cursor: pointer;
  box-shadow: -2px 0 6px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease;
  animation: pulse 2s infinite;
  align-items: center;
  justify-content: center;
}

.menu-indicator:hover {
  width: 30px;
  box-shadow: -3px 0 8px rgba(0, 0, 0, 0.3);
}

.indicator-arrow {
  color: white;
  font-size: 16px;
  font-weight: bold;
  position: relative;
  z-index: 2;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  transform: rotate(180deg);
}

.touch-area {
  position: absolute;
  top: -15px;
  left: -20px;
  bottom: -15px;
  right: -5px;
  z-index: 1;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(216, 103, 12, 0.4);
  }

  70% {
    box-shadow: 0 0 0 6px rgba(216, 103, 12, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(216, 103, 12, 0);
  }
}

.viewer-readonly-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 1rem;
  margin: -0.5rem 0 1rem;
  border-radius: 0.5rem;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
  font-size: 0.875rem;
  line-height: 1.4;
}

.viewer-readonly-banner__icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
  opacity: 0.9;
}

.license-invalid-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 1rem;
  margin: -0.5rem 0 1rem;
  border-radius: 0.5rem;
  background: #fee2e2;
  border: 1px solid #f87171;
  color: #991b1b;
  font-size: 0.875rem;
  line-height: 1.4;
}

.license-invalid-banner__icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.license-block-link {
  display: inline-block;
  margin-top: 0.75rem;
  color: #c2410c;
  font-weight: 600;
  text-decoration: none;
}

.license-block-link:hover {
  text-decoration: underline;
}

.main-content {
  flex: 1;
  padding: 2rem;
  background-color: #f5f5f5;
  margin-left: var(--backoffice-sidebar-width);
  min-height: 100vh;
  width: calc(100% - var(--backoffice-sidebar-width));
  box-sizing: border-box;
  overflow-x: hidden;
  transition: transform 0.3s ease, margin-left 0.3s ease, width 0.3s ease;
}

.main-content--orphan-admin {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 100vh;
  max-height: 100vh;
  overflow: hidden;
}

.main-content-inner {
  flex: 1;
  min-height: 0;
}

.main-content-inner--blurred {
  overflow: hidden;
  filter: blur(5px);
  pointer-events: none;
  user-select: none;
}

.main-content-block-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 1rem 2rem 2rem;
  box-sizing: border-box;
  pointer-events: none;
}

.main-content-block-card {
  pointer-events: auto;
  width: 100%;
  max-width: 36rem;
  margin-top: 0.25rem;
  background: #fff;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(216, 103, 12, 0.35);
}

.main-content-block-title {
  margin: 0 0 0.75rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.35;
}

.main-content-block-text {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: #444;
}

@media (max-width: 768px) {
  .menu-indicator {
    display: flex;
  }

  .main-content {
    margin-left: 0;
    width: 100%;
  }
}

@media (max-width: 640px) {
  .main-content {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding-top: 1rem;
  }

  .menu-indicator {
    top: 5px;
    height: 36px;
    width: 25px;
  }

  .indicator-arrow {
    font-size: 12px;
    margin-right: -1px;
  }
}
</style>